import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Video, 
  Users, 
  Mic, 
  FileText, 
  Calendar, 
  Award, 
  Star, 
  Target, 
  TrendingUp, 
  Share2,
  MessageSquare,
  Heart,
  Zap,
  Gift,
  Handshake,
  Lightbulb
} from 'lucide-react';
import { cn } from '@/lib/utils';

const ICON_OPTIONS = [
  { name: 'Video', icon: Video },
  { name: 'Users', icon: Users },
  { name: 'Mic', icon: Mic },
  { name: 'FileText', icon: FileText },
  { name: 'Calendar', icon: Calendar },
  { name: 'Award', icon: Award },
  { name: 'Star', icon: Star },
  { name: 'Target', icon: Target },
  { name: 'TrendingUp', icon: TrendingUp },
  { name: 'Share2', icon: Share2 },
  { name: 'MessageSquare', icon: MessageSquare },
  { name: 'Heart', icon: Heart },
  { name: 'Zap', icon: Zap },
  { name: 'Gift', icon: Gift },
  { name: 'Handshake', icon: Handshake },
  { name: 'Lightbulb', icon: Lightbulb },
];

interface CollaborationTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any) => void;
  initialData?: any;
}

export const CollaborationTypeDialog = ({ open, onOpenChange, onSubmit, initialData }: CollaborationTypeDialogProps) => {
  const { register, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: { name: '', description: '', prerequisites: '', icon: '', status: 'active' }
  });

  const status = watch('status');
  const [selectedIcon, setSelectedIcon] = useState('');

  // Reset form when initialData changes or dialog opens
  useEffect(() => {
    if (open) {
      if (initialData) {
        reset({
          name: initialData.name || '',
          description: initialData.description || '',
          prerequisites: initialData.prerequisites || '',
          icon: initialData.icon || '',
          status: initialData.status || 'active'
        });
        setSelectedIcon(initialData.icon || '');
      } else {
        reset({
          name: '',
          description: '',
          prerequisites: '',
          icon: '',
          status: 'active'
        });
        setSelectedIcon('');
      }
    }
  }, [open, initialData, reset]);

  const handleIconSelect = (iconName: string) => {
    setSelectedIcon(iconName);
    setValue('icon', iconName);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitleWithBug 
            popupName={initialData ? "Edit Collaboration Type" : "Create Collaboration Type"}
            popupContext="/collaboration-types"
            isOpen={open}
          >
            {initialData ? 'Edit' : 'Create'} Collaboration Type
          </DialogTitleWithBug>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input id="name" {...register('name', { required: true })} placeholder="e.g., LinkedIn Top Voice Program" />
          </div>

          <div>
            <Label>Icon</Label>
            <div className="grid grid-cols-8 gap-2 p-3 bg-muted rounded-md">
              {ICON_OPTIONS.map(({ name, icon: Icon }) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => handleIconSelect(name)}
                  className={cn(
                    "p-2 rounded-md hover:bg-background transition-colors",
                    selectedIcon === name && "bg-primary text-primary-foreground"
                  )}
                  title={name}
                >
                  <Icon className="h-5 w-5" />
                </button>
              ))}
            </div>
            {selectedIcon && (
              <p className="text-xs text-muted-foreground mt-1">Selected: {selectedIcon}</p>
            )}
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" {...register('description')} placeholder="Describe this collaboration type..." />
          </div>

          <div>
            <Label htmlFor="prerequisites">Prerequisites</Label>
            <Textarea id="prerequisites" {...register('prerequisites')} placeholder="List any requirements or prerequisites..." />
          </div>

          <div>
            <Label htmlFor="status">Status</Label>
            <Select defaultValue={initialData?.status || 'active'} onValueChange={(value) => setValue('status', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit">{initialData ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
